import React, { useState } from 'react';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Alert from '@cloudscape-design/components/alert';
import Button from '@cloudscape-design/components/button';
import Badge from '@cloudscape-design/components/badge';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import { getAwsConfig } from '../config/appConfig';
import { getAuthService } from '../services';
import { useAuth } from '../auth/AuthContext';

// One top-level language choice drives every step's snippet.
const LANGS = ['cURL', 'Python', 'Node.js', 'Java'] as const;
type Lang = (typeof LANGS)[number];

// A ready-to-edit sample ASN body for Channel A (JSON via API). This is the
// supplier-facing payload: just the `asn` array, matching the published AsnFile
// contract. The file envelope (fileInformation/fileId/creationDate) is NOT
// included on purpose — the server owns and stamps it on every channel, so a
// supplier never provides it. Suppliers download this, replace the example
// values with their own, and POST it to /v1/asn.
const SAMPLE_ASN = {
    asn: [
        {
            asnNumber: 'ASN-2026-000123456',
            asnDate: '1787616000',
            shipDate: '1787616100',
            expectedDeliveryDate: '1787616200',
            shipFrom: {
                partyId: 'VENDOR-1001',
                name: 'Acme Manufacturing Co.',
            },
            shipTo: {
                address: {
                    line1: '20529 62nd Ave S',
                    city: 'Kent',
                    state: 'WA',
                    postalCode: '98032',
                    country: 'US',
                },
            },
            totals: {
                palletCount: 1,
                cartonCount: 1,
                unitCount: 3,
            },
            pallets: [
                {
                    palletSequence: 1,
                    palletId: 'SSCC-00614141123456789012',
                    cartons: [
                        {
                            cartonSequence: 1,
                            cartonId: 'SSCC-00614141987654321098',
                            cartonType: 'MASTER_CASE',
                            purchaseOrders: [
                                {
                                    poNumber: 'PO-4501234567',
                                    lines: [
                                        {
                                            lineNumber: 10,
                                            partno: 'ADPT-USBC-3FT',
                                            itemDescription:
                                                'Tripp Lite 3ft USB-C to USB-A Adapter',
                                            shippedquantity: '3',
                                            unitPrice: '10',
                                            countryOfOrigin: 'CN',
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
            ],
        },
    ],
};

// Trigger a client-side download of the sample ASN as asn.json. Mirrors the
// Blob + object-URL pattern used by the ASN screen's template downloads.
function downloadSampleAsn() {
    const blob = new Blob([JSON.stringify(SAMPLE_ASN, null, 2)], {
        type: 'application/json;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'asn.json';
    a.click();
    URL.revokeObjectURL(url);
}

// A dark monospace code block for copy-ready snippets. Cloudscape has no plain
// code-block primitive here, so this stays a styled <pre>. The inner <code>
// explicitly resets background/color so Cloudscape's inline-code styling (which
// otherwise gives it a light "highlighted" chip inside the HelpPanel) can't
// override the dark block. Scrolls horizontally so long lines never widen the
// surrounding layout.
function Code({ children }: { children: string }) {
    return (
        <pre
            style={{
                background: '#0f1b2d',
                color: '#e6edf3',
                padding: '12px 14px',
                borderRadius: 8,
                overflowX: 'auto',
                maxWidth: '100%',
                fontSize: 12.5,
                lineHeight: 1.5,
                margin: 0,
                whiteSpace: 'pre',
                boxSizing: 'border-box',
                fontFamily:
                    "'JetBrains Mono', 'Fira Code', ui-monospace, SFMono-Regular, Menlo, monospace",
            }}
        >
            <code
                style={{
                    background: 'transparent',
                    color: 'inherit',
                    padding: 0,
                    border: 'none',
                    borderRadius: 0,
                    font: 'inherit',
                    boxShadow: 'none',
                }}
            >
                {children}
            </code>
        </pre>
    );
}

/**
 * Post-login guide for the machine-to-machine (M2M) ASN integration channels.
 * Written for a general audience: a plain-English overview, a checklist of
 * what's needed, and copy-ready snippets. A single language selector at the top
 * switches every step's code between cURL / Python / Node.js / Java.
 *
 * Real per-stage endpoints come from runtime config. The signed-in supplier's
 * clientId is shown at the top (safe to display); the clientSecret is issued
 * once at approval and is never exposed to the app.
 */
export default function IntegrationGuide() {
    const [lang, setLang] = useState<Lang>('cURL');
    const { user } = useAuth();

    // Informational page; never let missing/late config crash it.
    let tokenEndpoint = '<oauth-token-endpoint>';
    let apiBase = '<asn-api-endpoint>';
    try {
        const cfg = getAwsConfig();
        tokenEndpoint = cfg.oauthTokenEndpoint ?? tokenEndpoint;
        apiBase = (cfg.asnApiEndpoint ?? apiBase).replace(/\/+$/, '');
    } catch {
        // Runtime config not available; fall through with placeholders.
    }
    const api = apiBase;

    // Reveal-on-demand M2M credentials for the signed-in supplier. The secret
    // is fetched only when the user clicks "Reveal", never eagerly.
    const [revealed, setRevealed] = useState<{ clientId: string; clientSecret: string } | null>(
        null,
    );
    const [revealing, setRevealing] = useState(false);
    const [revealError, setRevealError] = useState<string | null>(null);

    const revealSecret = async () => {
        setRevealing(true);
        setRevealError(null);
        try {
            setRevealed(await getAuthService().getMyCredentials());
        } catch (e) {
            setRevealError(e instanceof Error ? e.message : 'Could not load your credentials.');
        } finally {
            setRevealing(false);
        }
    };

    // Use the signed-in supplier's real clientId in the Step 1 snippets when we
    // have it (prefer the revealed value, else the token's clientId claim);
    // otherwise keep the generic placeholder.
    const clientIdSample = revealed?.clientId ?? user?.clientId ?? '<client_id>';

    // ── Step 1: get an access token ───────────────────────────────────────────
    const tokenSnippets: Record<Lang, string> = {
        'cURL': `curl -X POST ${tokenEndpoint} \\
  -H "Content-Type: application/x-www-form-urlencoded" \\
  -u "${clientIdSample}:<client_secret>" \\
  -d "grant_type=client_credentials&scope=asn/submit"`,
        'Python': `import requests

resp = requests.post(
    "${tokenEndpoint}",
    auth=("${clientIdSample}", "<client_secret>"),
    data={"grant_type": "client_credentials",
          "scope": "asn/submit"},
)
access_token = resp.json()["access_token"]`,
        'Node.js': `const creds = Buffer
  .from("${clientIdSample}:<client_secret>")
  .toString("base64");

const resp = await fetch("${tokenEndpoint}", {
  method: "POST",
  headers: {
    "Content-Type": "application/x-www-form-urlencoded",
    Authorization: \`Basic \${creds}\`,
  },
  body: new URLSearchParams({
    grant_type: "client_credentials",
    scope: "asn/submit",
  }),
});
const { access_token } = await resp.json();`,
        'Java': `String creds = Base64.getEncoder().encodeToString(
    "${clientIdSample}:<client_secret>".getBytes());

HttpRequest req = HttpRequest.newBuilder()
    .uri(URI.create("${tokenEndpoint}"))
    .header("Content-Type",
            "application/x-www-form-urlencoded")
    .header("Authorization", "Basic " + creds)
    .POST(HttpRequest.BodyPublishers.ofString(
        "grant_type=client_credentials"
        + "&scope=asn/submit"))
    .build();

HttpResponse<String> resp = HttpClient.newHttpClient()
    .send(req, HttpResponse.BodyHandlers.ofString());
// parse "access_token" from resp.body()`,
    };

    // ── Step 2: submit a JSON ASN via the API ──────────────────────────────────
    const apiSnippets: Record<Lang, string> = {
        'cURL': `curl -X POST ${api}/v1/asn \\
  -H "Authorization: Bearer <access_token>" \\
  -H "Content-Type: application/json" \\
  --data @asn.json

# 202 Accepted:
# { "status": "ACCEPTED",
#   "submissionId": "...",
#   "asnNumbers": ["ASN-2026-000123456"],
#   "receivedAt": "..." }`,
        'Python': `import json, requests

with open("asn.json") as f:
    asn = json.load(f)

resp = requests.post(
    "${api}/v1/asn",
    headers={"Authorization": f"Bearer {access_token}"},
    json=asn,
)
print(resp.status_code, resp.json())
# 202, { submissionId, asnNumbers: [...], ... }`,
        'Node.js': `import { readFile } from "node:fs/promises";

const asn = JSON.parse(
  await readFile("asn.json", "utf8"));

const resp = await fetch("${api}/v1/asn", {
  method: "POST",
  headers: {
    Authorization: \`Bearer \${access_token}\`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify(asn),
});
console.log(resp.status, await resp.json());
// 202, { submissionId, ... }`,
        'Java': `String asn = Files.readString(
    Path.of("asn.json"));

HttpRequest req = HttpRequest.newBuilder()
    .uri(URI.create("${api}/v1/asn"))
    .header("Authorization", "Bearer " + accessToken)
    .header("Content-Type", "application/json")
    .POST(HttpRequest.BodyPublishers.ofString(asn))
    .build();

HttpResponse<String> resp = HttpClient.newHttpClient()
    .send(req, HttpResponse.BodyHandlers.ofString());
// 202: { submissionId, asnNumbers, receivedAt }`,
    };

    // ── Step 3: upload a file via presigned URL ─────────────────────────────────
    const presignSnippets: Record<Lang, string> = {
        'cURL': `# 1) Ask for a short-lived upload URL
curl -X POST ${api}/v1/asn/presign \\
  -H "Authorization: Bearer <access_token>" \\
  -H "Content-Type: application/json" \\
  -d '{"fileName":"asn-123.csv",
       "contentType":"text/csv"}'
# -> { "uploadUrl": "https://...",
#      "s3Key": "...",
#      "expiresInSeconds": 900 }

# 2) Upload the file to the returned uploadUrl.
#    Use -T (upload-file): it sends a fixed Content-Length.
#    Do NOT use --data-binary for large files — curl streams it as
#    "Transfer-Encoding: chunked", which S3 rejects with 501.
curl -X PUT "<uploadUrl>" \\
  -H "Content-Type: text/csv" \\
  -T asn-123.csv`,
        'Python': `import requests

# 1) Ask for a presigned upload URL
presign = requests.post(
    "${api}/v1/asn/presign",
    headers={"Authorization": f"Bearer {access_token}"},
    json={"fileName": "asn-123.csv",
          "contentType": "text/csv"},
).json()

# 2) Upload the file straight to storage.
#    Send bytes (f.read()) so requests sets a fixed Content-Length.
#    Passing the open file object streams chunked, which S3 rejects (501).
with open("asn-123.csv", "rb") as f:
    body = f.read()
requests.put(
    presign["uploadUrl"],
    data=body,
    headers={"Content-Type": "text/csv"},
)`,
        'Node.js': `import { readFile } from "node:fs/promises";

// 1) Ask for a presigned upload URL
const presign = await (await fetch(
  "${api}/v1/asn/presign",
  {
    method: "POST",
    headers: {
      Authorization: \`Bearer \${access_token}\`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      fileName: "asn-123.csv",
      contentType: "text/csv",
    }),
  },
)).json();

// 2) Upload the file straight to storage
await fetch(presign.uploadUrl, {
  method: "PUT",
  headers: { "Content-Type": "text/csv" },
  body: await readFile("asn-123.csv"),
});`,
        'Java': `// 1) Ask for a presigned upload URL
HttpRequest presignReq = HttpRequest.newBuilder()
    .uri(URI.create("${api}/v1/asn/presign"))
    .header("Authorization", "Bearer " + accessToken)
    .header("Content-Type", "application/json")
    .POST(HttpRequest.BodyPublishers.ofString(
        "{\\"fileName\\":\\"asn-123.csv\\","
        + "\\"contentType\\":\\"text/csv\\"}"))
    .build();
String uploadUrl = /* parse "uploadUrl" */ "";

// 2) Upload the file straight to storage
HttpRequest putReq = HttpRequest.newBuilder()
    .uri(URI.create(uploadUrl))
    .header("Content-Type", "text/csv")
    .PUT(HttpRequest.BodyPublishers.ofFile(
        Path.of("asn-123.csv")))
    .build();
HttpClient.newHttpClient()
    .send(putReq, HttpResponse.BodyHandlers.ofString());`,
    };

    return (
        <SpaceBetween size="l">
            <Box color="text-body-secondary">
                An ASN (Advance Shipping Notice) tells us what you&rsquo;re shipping before it
                arrives. If you want <strong>programmatic access</strong> — sending ASNs directly
                from your own systems, without the manual upload screens — please follow this guide.
                It walks you through authenticating and submitting over either of two channels; pick
                the one that fits your data. You only need one.
            </Box>

            {/* Your credentials — specific to this signed-in login. */}
            <Container header={<Header variant="h3">Your credentials</Header>}>
                {user ? (
                    <SpaceBetween size="s">
                        <Box color="text-body-secondary">
                            Signed in as <strong>{user.email}</strong>
                            {user.company ? (
                                <>
                                    {' '}
                                    (<strong>{user.company}</strong>)
                                </>
                            ) : null}
                            . The credentials below are specific to this login.
                        </Box>
                        <Box fontWeight="bold">clientId</Box>
                        {revealed?.clientId || user.clientId ? (
                            <Code>{revealed?.clientId ?? user.clientId ?? ''}</Code>
                        ) : (
                            <Box color="text-body-secondary">
                                Your <code>clientId</code> is issued when your account is approved.
                                Use <strong>Reveal my credentials</strong> below once approved.
                            </Box>
                        )}
                        <Box fontWeight="bold">clientSecret</Box>
                        {revealed ? (
                            <SpaceBetween size="s">
                                <Code>{revealed.clientSecret}</Code>
                                <Alert type="warning">
                                    Treat this like a password. It&rsquo;s shown here only because
                                    you asked to reveal it — don&rsquo;t share it or paste it where
                                    others can see. If it may be exposed, ask an administrator to
                                    rotate it.
                                </Alert>
                            </SpaceBetween>
                        ) : (
                            <SpaceBetween size="s">
                                <Box color="text-body-secondary">
                                    Your <code>clientSecret</code> is sensitive, so it&rsquo;s hidden
                                    by default. Reveal it only when you need to configure your
                                    integration.
                                </Box>
                                <Button loading={revealing} onClick={revealSecret}>
                                    Reveal my credentials
                                </Button>
                                {revealError ? <Alert type="error">{revealError}</Alert> : null}
                            </SpaceBetween>
                        )}
                    </SpaceBetween>
                ) : (
                    <Box color="text-body-secondary">
                        Sign in to see the <code>clientId</code> issued to your organization.
                    </Box>
                )}
            </Container>

            {/* Prerequisites */}
            <Container header={<Header variant="h3">Before you begin</Header>}>
                <SpaceBetween size="s">
                    <Box color="text-body-secondary">
                        You must pass a <strong>clientId</strong> and a <strong>clientSecret</strong>{' '}
                        on every integration. These are issued to your organization by the Supplier
                        Connect team — request them by emailing <strong>fortumel@amazon.com</strong>.
                        You will not be able to authenticate (Step 1) without both values.
                    </Box>
                    <Box color="text-body-secondary">
                        Keep the <code>clientSecret</code> confidential: store it in a secrets
                        manager, never commit it to source control, and rotate it immediately if it
                        is ever exposed.
                    </Box>
                    <Box color="text-body-secondary">
                        You will also need one of the following, plus the ability to make HTTPS
                        requests from your system:
                    </Box>
                    <Box color="text-body-secondary">
                        • <strong>Channel A</strong> — your ASN as a JSON body, submitted in a single
                        API call.
                    </Box>
                    <Box color="text-body-secondary">
                        • <strong>Channel B</strong> — an ASN file, uploaded in a 2-step process
                        where you supply the exact file name.
                    </Box>
                </SpaceBetween>
            </Container>

            {/* Language selector — drives every code sample below. */}
            <SpaceBetween size="xs">
                <Box color="text-body-secondary">Show examples in</Box>
                <SegmentedControl
                    selectedId={lang}
                    onChange={({ detail }) => setLang(detail.selectedId as Lang)}
                    options={LANGS.map((l) => ({ id: l, text: l }))}
                />
            </SpaceBetween>

            {/* Step 1 — shared prerequisite for both channels. */}
            <Container header={<Header variant="h3">Step 1 — Get an access token</Header>}>
                <SpaceBetween size="m">
                    <Box color="text-body-secondary">
                        Both channels authenticate the same way. Exchange the{' '}
                        <strong>clientId</strong> and <strong>clientSecret</strong> you received from{' '}
                        <strong>fortumel@amazon.com</strong> for a short-lived bearer token using the
                        OAuth2 client-credentials grant with the <code>asn/submit</code> scope. Send
                        this token in the <code>Authorization: Bearer</code> header on every request.
                        Tokens expire in one hour — request a new one when it does.
                    </Box>
                    <Code>{tokenSnippets[lang]}</Code>
                </SpaceBetween>
            </Container>

            <Header variant="h2">Step 2 — Send your ASN (choose one channel)</Header>

            {/* Channel A: JSON via API */}
            <Container
                header={
                    <Header variant="h3" info={<Badge color="blue">Channel A</Badge>}>
                        Submit JSON via the API
                    </Header>
                }
            >
                <SpaceBetween size="m">
                    <Box color="text-body-secondary">
                        A <strong>single request</strong>. POST your ASN as a JSON body to{' '}
                        <code>/v1/asn</code>. We validate it against the ASN schema synchronously and
                        respond immediately:
                    </Box>
                    <Box color="text-body-secondary">
                        • <strong>202 Accepted</strong> — the ASN passed validation. The response
                        includes a <code>submissionId</code> and <code>asnNumbers</code> (the list of
                        ASN numbers in the payload).
                    </Box>
                    <Box color="text-body-secondary">
                        • <strong>400 Rejected</strong> — validation failed. The response lists the
                        errors so you can correct and resubmit.
                    </Box>
                    <Box color="text-body-secondary">
                        Best for small, structured payloads generated directly by your system.
                    </Box>
                    <Box color="text-body-secondary">
                        Not sure of the shape? Download a ready-made{' '}
                        <code>asn.json</code>, replace the example values with your own, and POST it.
                        You only send the ASN content — the file envelope is added by the service.
                    </Box>
                    <Button iconName="download" onClick={downloadSampleAsn}>
                        Download sample asn.json
                    </Button>
                    <Code>{apiSnippets[lang]}</Code>
                </SpaceBetween>
            </Container>

            {/* Channel B: file via presigned URL */}
            <Container
                header={
                    <Header variant="h3" info={<Badge color="blue">Channel B</Badge>}>
                        Upload a file via a presigned URL
                    </Header>
                }
            >
                <SpaceBetween size="m">
                    <Box color="text-body-secondary">
                        A <strong>2-step</strong> process. Best for larger payloads and file
                        exports. In this channel you provide the <code>fileName</code>, and it is
                        verified during the upload — so the file name must be given correctly.
                    </Box>
                    <Box color="text-body-secondary">
                        <strong>Step 2a — Request an upload URL.</strong> POST to{' '}
                        <code>/v1/asn/presign</code> and pass the exact <code>fileName</code> you
                        intend to upload (for example <code>asn-123.csv</code>), along with its{' '}
                        <code>contentType</code>. You get back a one-time <code>uploadUrl</code>{' '}
                        (valid ~15 minutes) and a <code>submissionId</code>.
                    </Box>
                    <Box color="text-body-secondary">
                        <strong>Step 2b — Upload the file.</strong> Send the file to that{' '}
                        <code>uploadUrl</code> with an HTTP <code>PUT</code>. A background job then
                        validates the file and updates its status (visible in the ASN screen).
                    </Box>
                    <Alert type="warning">
                        <strong>Get the file name right.</strong> AWS verifies the{' '}
                        <code>fileName</code> during the upload, so it must be correct: it must end
                        in <code>.csv</code> or <code>.xlsx</code>; the <code>contentType</code> must
                        match the extension (<code>text/csv</code> for <code>.csv</code>); and you
                        must use the same <code>fileName</code> and <code>contentType</code> in Step
                        2a and Step 2b.
                    </Alert>
                    <Code>{presignSnippets[lang]}</Code>
                </SpaceBetween>
            </Container>
        </SpaceBetween>
    );
}
